// Typed Contacts API client for the web layer (three-tier client model
// Stage 1, ADR-0015).
//
// Wraps the `contactsContract` endpoints via the shared `api` client and
// exposes react-query hooks. Mutations route their `{ status, body }`
// envelope through `runStaffEndpoint` and invalidate `contactsKeys.all` so
// the directory and the detail view re-render together.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, runStaffEndpoint, staffErrorMessage } from '@/lib/api';
import { clientBookKeys } from '@/lib/client-book/hooks';
import type {
    ContactDetail,
    ContactKind,
    ContactLifecycleAction,
    CreateContactRequest,
    CreateOrganisationContactRequest,
    DuplicateCandidatesResponse,
    ListContactsResponse,
    StaffActionResult,
    UpdateContactRequest,
    UpdateOrganisationContactRequest,
} from '@septima/contract';

export interface ContactsQuery {
    search?: string;
    contactKind?: ContactKind;
    status?: 'draft' | 'active' | 'closed';
}

export interface DuplicateProbe {
    contactKind: ContactKind;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    organisationName?: string;
}

export const contactsKeys = {
    all: ['contacts'] as const,
    list: (q?: ContactsQuery) => ['contacts', 'list', q ?? {}] as const,
    detail: (contactId: string) => ['contacts', 'detail', contactId] as const,
    duplicates: (probe: DuplicateProbe) => ['contacts', 'duplicates', probe] as const,
};

function assertOk(result: StaffActionResult): StaffActionResult {
    if (!result.ok) throw new Error(result.error ?? 'Request failed');
    return result;
}

// ---------------------------------------------------------------------------
// Contact delete (SEP-244)
// ---------------------------------------------------------------------------

/**
 * The reason a contact delete failed, as the page must show it.
 *
 * `runStaffEndpoint` flattens a 400 body to `{ ok: false, error }`, and it
 * keeps the sentence only. The refusal of a contact delete carries two more
 * facts: the machine-readable `code` and, for the relationship refusal, the
 * count of the relationships that block the delete. The page needs both, so
 * this hook reads the raw body beside the flattened envelope.
 */
export interface ContactDeleteRefusal {
    /** For example `CONTACT_HAS_CURRENT_RELATIONSHIPS`. Null when absent. */
    code: string | null;
    /** The sentence the server wrote. The page always shows this. */
    message: string;
    /** How many current relationships block the delete. Null when absent. */
    currentRelationshipCount: number | null;
}

/** The refusal code that names a contact with current relationships. */
export const CONTACT_HAS_CURRENT_RELATIONSHIPS = 'CONTACT_HAS_CURRENT_RELATIONSHIPS';

/** A failed contact delete, with the refusal attached. */
export class ContactDeleteError extends Error {
    readonly refusal: ContactDeleteRefusal;
    constructor(refusal: ContactDeleteRefusal) {
        super(refusal.message);
        this.name = 'ContactDeleteError';
        this.refusal = refusal;
    }
}

/** Read the refusal out of the raw 400 body. `fallback` is the flattened
 *  message, so the page always has a sentence to show. */
function readRefusal(body: unknown, fallback: string): ContactDeleteRefusal {
    const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : undefined;
    const code = typeof record?.code === 'string' ? record.code : null;
    const count = record?.currentRelationshipCount;
    return {
        code,
        message: fallback,
        currentRelationshipCount: typeof count === 'number' ? count : null,
    };
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useContacts(query?: ContactsQuery, options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: contactsKeys.list(query),
        enabled: options?.enabled ?? true,
        queryFn: async (): Promise<ListContactsResponse> => {
            const res = await api.contacts.listContacts({ query: query ?? {} });
            if (res.status !== 200) throw new Error(`Contacts failed: ${res.status}`);
            return res.body;
        },
        staleTime: 10_000,
    });
}

export function useContactDetail(contactId: string | undefined) {
    return useQuery({
        queryKey: contactsKeys.detail(contactId ?? ''),
        enabled: Boolean(contactId),
        queryFn: async (): Promise<ContactDetail> => {
            const res = await api.contacts.getContact({ params: { contactId: contactId! } });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        staleTime: 5_000,
    });
}

/**
 * Advisory duplicate candidates for a proposed contact (never blocking —
 * business answer #10). Enabled only once the probe carries enough to match:
 * a person needs name + date of birth, an organisation needs its name.
 */
export function useDuplicateCandidates(probe: DuplicateProbe) {
    const enabled =
        probe.contactKind === 'person'
            ? Boolean((probe.firstName || probe.lastName) && probe.dateOfBirth)
            : Boolean(probe.organisationName);
    return useQuery({
        queryKey: contactsKeys.duplicates(probe),
        enabled,
        queryFn: async (): Promise<DuplicateCandidatesResponse> => {
            const res = await api.contacts.duplicateCandidates({ query: probe });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        staleTime: 10_000,
    });
}

// ---------------------------------------------------------------------------
// Mutations (via runStaffEndpoint)
// ---------------------------------------------------------------------------

export function useCreateContact() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateContactRequest): Promise<string> => {
            const result = assertOk(
                await runStaffEndpoint(api.contacts.createContact({ body: input })),
            );
            return (result as { contactId?: string }).contactId ?? '';
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKeys.all }),
    });
}

export function useUpdateContact() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            contactId: string;
            patch: UpdateContactRequest;
        }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.contacts.updateContact({
                        params: { contactId: input.contactId },
                        body: input.patch,
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKeys.all }),
    });
}

/**
 * Give many Contacts one owner in a single step (SEP-232).
 *
 * Returns the assigned count and the skipped rows rather than `void`, because
 * a batch can partly succeed: the Action refuses a row that carries an active
 * Relationship, and the caller must be able to say so. The narrow cast off
 * `StaffActionResult` matches how `ClientBooksCard` reads its own extra
 * response fields.
 */
export function useAssignContactOwnersBulk() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            contactIds: string[];
            ownerImUserId: string;
        }): Promise<{ assigned: number; skipped: { contactId: string; reason: string }[] }> => {
            const result = await runStaffEndpoint(
                api.contacts.assignContactOwnersBulk({
                    body: { contactIds: input.contactIds, ownerImUserId: input.ownerImUserId },
                }),
            );
            assertOk(result);
            const body = result as {
                assigned?: number;
                skipped?: { contactId: string; reason: string }[];
            };
            return { assigned: body.assigned ?? 0, skipped: body.skipped ?? [] };
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKeys.all }),
    });
}

/** Operator-mediated book move (ADR-0015 D0.2) — reassign-contact-owner Action. */
export function useReassignContactOwner() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { contactId: string; ownerImUserId: string }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.contacts.reassignContactOwner({
                        params: { contactId: input.contactId },
                        body: { ownerImUserId: input.ownerImUserId },
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKeys.all }),
    });
}

/**
 * Share one Contact record with a SECOND IM, and take the share back
 * (SEP-245) — the add-contact-co-owner and remove-contact-co-owner Actions.
 *
 * A share is not a move. `ownerImUserId` stays as it is, so the client work
 * keeps the one book that owns it, and the co-owner gets the record alone.
 */
export function useAddContactCoOwner() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { contactId: string; imUserId: string }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.contacts.addContactCoOwner({
                        params: { contactId: input.contactId },
                        body: { imUserId: input.imUserId },
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKeys.all }),
    });
}

/** Take a shared Contact record back off a second IM (SEP-245). */
export function useRemoveContactCoOwner() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { contactId: string; imUserId: string }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.contacts.removeContactCoOwner({
                        params: { contactId: input.contactId, imUserId: input.imUserId },
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKeys.all }),
    });
}

export function useSetContactStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            contactId: string;
            action: ContactLifecycleAction;
        }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.contacts.setContactStatus({
                        params: { contactId: input.contactId },
                        body: { action: input.action },
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKeys.all }),
    });
}

export function useCreateOrganisationContact() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateOrganisationContactRequest): Promise<string> => {
            const result = assertOk(
                await runStaffEndpoint(api.contacts.createOrganisationContact({ body: input })),
            );
            return (result as { contactId?: string }).contactId ?? '';
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKeys.all }),
    });
}

export function useUpdateOrganisationContact() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            contactId: string;
            patch: UpdateOrganisationContactRequest;
        }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.contacts.updateOrganisationContact({
                        params: { contactId: input.contactId },
                        body: input.patch,
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKeys.all }),
    });
}

export function useSetOrganisationContactStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            contactId: string;
            action: ContactLifecycleAction;
        }): Promise<void> => {
            assertOk(
                await runStaffEndpoint(
                    api.contacts.setOrganisationContactStatus({
                        params: { contactId: input.contactId },
                        body: { action: input.action },
                    }),
                ),
            );
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKeys.all }),
    });
}

/**
 * Delete a contact, person or organisation (SEP-244).
 *
 * The delete is SOFT: the contact leaves every staff surface, and the firm
 * keeps the record in the background. An administrator retrieves it from the
 * the Admin shell card for six months.
 *
 * The call goes through `runStaffEndpoint` for the request-id capture and the
 * error tracking, and the hook then reads the raw body for the refusal detail
 * that the flattened envelope drops.
 */
export function useDeleteContact() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            contactId: string;
            contactKind: ContactKind;
        }): Promise<void> => {
            const params = { contactId: input.contactId };
            const response = await (input.contactKind === 'person'
                ? api.contacts.deleteContact({ params, body: {} })
                : api.contacts.deleteOrganisationContact({ params, body: {} }));
            const result = await runStaffEndpoint(Promise.resolve(response));
            if (!result.ok) {
                throw new ContactDeleteError(
                    readRefusal(response.body, result.error ?? 'The delete failed.'),
                );
            }
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: contactsKeys.all });
            // The Client Book reads the contact link of a client, so a deleted
            // contact changes what those rows show.
            void qc.invalidateQueries({ queryKey: clientBookKeys.all });
        },
    });
}
